import { account } from '../session/session.store';
import { clock } from '../time/clock';
import { openDeviceDatabaseExclusive } from '../outbox/device-database';

/**
 * 036 (issue 222, FR-023) — "Keep 'last opened' per page in the practitioner's device database …
 * written when a page is opened." `fetch-ahead.ts` is the one reader: it prefetches every non-hidden
 * context, non-completed project and every tag, most recently opened first, and pages never opened
 * come after, in the order the lists give (research R-010's own scope note named this as later work;
 * this file is that work).
 *
 * **One key format, stated once.** `context:<id>` / `project:<id>` / `tag:<name>` — a plain string so
 * `device-database.ts`'s `meta` store (already schemaed, empty, for exactly this) needs no opinion
 * about what a key means; only this file constructs or parses one.
 *
 * **Opened, not fetched.** This is written from a page's own `load()`/`loadTag()` — the moment the
 * practitioner asks to SEE that container or tag — never from `fetch-ahead.ts` itself finishing a
 * prefetch. A page fetch-ahead reaches first must not look "recently opened" merely because this
 * cycle's own background work touched it; only the practitioner's own navigation counts.
 *
 * **Best-effort, like every other write to this database `storing-client.ts` already makes for next
 * time.** A device that cannot store (FR-013), or one with no practitioner known yet, loses only the
 * ORDERING fetch-ahead would have used — never the page load itself, which does not await this.
 */
export type OpenedKind = 'context' | 'project' | 'tag';

/** Exported for `fetch-ahead.spec.ts` and this file's own spec — the one place the format is written. */
export function openedKey(kind: OpenedKind, ref: number | string): string {
  return `${kind}:${ref}`;
}

/**
 * Fire-and-forget from a page's own load — callers never `await` this (mirrors `storing-client.ts`'s
 * own `queueWrite`, not awaited for the identical reason: a device write must never hold up the
 * screen it is a side effect of).
 */
export async function recordPageOpened(kind: OpenedKind, ref: number | string): Promise<void> {
  const practitionerId = account.get()?.practitionerId;
  if (practitionerId == null) return;
  try {
    const db = await openDeviceDatabaseExclusive(practitionerId);
    try {
      await db.recordOpened(openedKey(kind, ref), clock().toISOString());
    } finally {
      db.close();
    }
  } catch {
    // The device would not keep this either way (FR-013) — no worse than never having recorded it.
  }
}

/**
 * `fetch-ahead.ts`'s own read: every entry ever recorded for this practitioner, as epoch
 * milliseconds — a `Map` rather than the raw `Record`, since every caller immediately compares two
 * entries' recency and a `Map` is what a sort-by-value reaches for. Absent for a key never opened,
 * which `fetch-ahead.ts` reads as "no evidence, comes after everything that has one" — never as `0`,
 * which would sort a never-opened page as though it were opened at the epoch, ahead of nothing.
 */
export async function openedTimes(practitionerId: number | string): Promise<ReadonlyMap<string, number>> {
  const db = await openDeviceDatabaseExclusive(practitionerId);
  try {
    const raw = await db.openedLog();
    return new Map(Object.entries(raw).map(([key, when]) => [key, Date.parse(when)]));
  } finally {
    db.close();
  }
}
